import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Field, inputClass, timeAgo, useAdminApi, useConfirm, useToast } from './ui';
import { FLAG_REASONS, reasonLabel } from './flagReasons';

const STATUS_FILTERS = [
  ['open', 'Open'],
  ['fixed', 'Fixed'],
  ['dismissed', 'Dismissed'],
  ['all', 'All'],
];
const STATUS_COLORS = { open: 'red', fixed: 'green', dismissed: 'gray' };

function AdminFlags({ regions = [], reload }) {
  const api = useAdminApi();
  const toast = useToast();
  const [flags, setFlags] = useState(null);
  const [filter, setFilter] = useState('open');
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api('/admin/listing-flags?status=all');
      setFlags(data.flags);
      setLoadError('');
    } catch (error) {
      setLoadError(error.message);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh this tab and the open-flag badge on the dashboard
  const refresh = useCallback(async () => {
    await load();
    reload({ silent: true });
  }, [load, reload]);

  const counts = useMemo(() => {
    const result = { open: 0, fixed: 0, dismissed: 0, all: 0 };
    for (const flag of flags || []) {
      result[flag.status]++;
      result.all++;
    }
    return result;
  }, [flags]);

  const visible = useMemo(() => (flags || []).filter((flag) => filter === 'all' || flag.status === filter), [flags, filter]);

  return (
    <div className="space-y-6">
      <ReviewLinks regions={regions} />

      <QuickFlag api={api} toast={toast} onAdded={refresh} />

      <ExportBar api={api} toast={toast} counts={counts} />

      <Card
        title="Flagged listings"
        actions={
          <div className="flex gap-1">
            {STATUS_FILTERS.map(([id, label]) => (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className={`px-2.5 py-1 text-xs rounded-full border ${filter === id ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                {label} {counts[id] > 0 && <span className="opacity-70">{counts[id]}</span>}
              </button>
            ))}
          </div>
        }
      >
        {loadError && <p className="text-sm text-red-700">{loadError}</p>}
        {!flags && !loadError && <p className="text-sm text-gray-500">Loading…</p>}
        {flags && visible.length === 0 && (
          <p className="text-sm text-gray-500">
            {filter === 'open' ? 'No open flags. Flag a listing above, or scan for suspects below.' : 'Nothing here.'}
          </p>
        )}
        {visible.length > 0 && (
          <ul className="divide-y divide-gray-100 -my-2">
            {visible.map((flag) => <FlagRow key={flag.id} flag={flag} api={api} toast={toast} onChange={refresh} />)}
          </ul>
        )}
      </Card>

      <Suspects api={api} toast={toast} onFlagged={refresh} />
    </div>
  );
}

// The dense review page is server-rendered, so these are plain links rather than router links.
// The CRA dev server only proxies /api, so in development link straight to the API server.
const SERVER_ORIGIN = process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '';

function ReviewLinks({ regions }) {
  const visible = regions.filter((region) => !region.hide);
  if (!visible.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3">
      <span className="font-medium">Review desk</span>
      <span className="text-gray-600">every upcoming listing on one dense page, with suspects marked and keyboard flagging:</span>
      {visible.map((region) => (
        <a key={region.id} href={`${SERVER_ORIGIN}/now/${region.urlName}/review`} target="_blank" rel="noreferrer" className="underline font-medium">{region.name} →</a>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flag a listing by pasting its link
// ---------------------------------------------------------------------------
function ReasonPicker({ value, onChange }) {
  const toggle = (key) => onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {FLAG_REASONS.map(([key, label]) => (
        <button
          type="button"
          key={key}
          onClick={() => toggle(key)}
          className={`px-2.5 py-1 text-xs rounded-full border ${value.includes(key) ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function QuickFlag({ api, toast, onAdded }) {
  const empty = { listing: '', reasons: [], note: '', duplicateOf: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { flag } = await api('/admin/listing-flags', {
        method: 'POST',
        body: { ...form, duplicateOf: form.reasons.includes('duplicate') ? form.duplicateOf : '' },
      });
      toast(`Flagged “${flag.listing ? flag.listing.name : 'listing'}”.`);
      setForm(empty);
      onAdded();
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Flag a listing">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Listing" hint="Paste a zeitgeists.org listing link or its UUID">
          <input
            className={inputClass}
            value={form.listing}
            placeholder="https://zeitgeists.org/listing/…"
            onChange={(e) => setForm({ ...form, listing: e.target.value })}
          />
        </Field>
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">What’s wrong?</span>
          <ReasonPicker value={form.reasons} onChange={(reasons) => setForm({ ...form, reasons })} />
        </div>
        {form.reasons.includes('duplicate') && (
          <Field label="Duplicate of" hint="Optional: the listing this one duplicates (the one to keep)">
            <input className={inputClass} value={form.duplicateOf} onChange={(e) => setForm({ ...form, duplicateOf: e.target.value })} />
          </Field>
        )}
        <Field label="Note" hint="What it should be, e.g. “show is 8–10 PM, doors 7:30”">
          <input className={inputClass} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
        <Button type="submit" variant="primary" busy={saving} disabled={!form.listing.trim() || !form.reasons.length}>Flag listing</Button>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------
function ExportBar({ api, toast, counts }) {
  const [status, setStatus] = useState('open');
  const [copying, setCopying] = useState(false);

  const copyForChat = async () => {
    setCopying(true);
    try {
      const data = await api(`/admin/listing-flags/export?format=chat&status=${status}`);
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast(`Copied ${data.issueCount} issue${data.issueCount === 1 ? '' : 's'}. Paste into a chat session to work through them.`);
    } catch (error) {
      toast(error.message || 'Couldn’t copy to the clipboard.', 'error');
    } finally {
      setCopying(false);
    }
  };

  return (
    <Card title="Export">
      <div className="flex flex-wrap items-center gap-2">
        <select className={`${inputClass} w-auto`} value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_FILTERS.map(([id, label]) => <option key={id} value={id}>{label} ({counts[id]})</option>)}
        </select>
        <Button variant="primary" busy={copying} disabled={!counts[status]} onClick={copyForChat}>Copy for chat</Button>
        <a className="text-sm underline text-gray-700 px-2" href={`/api/admin/listing-flags/export?format=chat&status=${status}&download=1`}>Download chat JSON</a>
        <a className="text-sm underline text-gray-700 px-2" href={`/api/admin/listing-flags/export?format=json&status=${status}&download=1`}>Full JSON</a>
        <a className="text-sm underline text-gray-700 px-2" href={`/api/admin/listing-flags/export?format=csv&status=${status}`}>CSV</a>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        “Copy for chat” groups issues by the integration and scraper file that made them, with the source URL, current values and your notes.
        Duplicate pairs you dismissed are included as confirmed non-duplicates.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// One flag
// ---------------------------------------------------------------------------
function ListingSummary({ listing, label }) {
  if (!listing) return <p className="text-sm text-gray-500">{label ? `${label}: ` : ''}listing was deleted</p>;
  return (
    <div className="min-w-0">
      {label && <span className="text-xs uppercase tracking-wide text-gray-500">{label} </span>}
      <Link to={`/listing/${listing.uuid}`} className="font-medium hover:underline">{listing.name}</Link>
      {listing.hidden && <span className="ml-2"><Badge color="purple">hidden</Badge></span>}
      <div className="text-sm text-gray-600">
        {listing.startLocal}{listing.endLocal && ` → ${listing.endLocal}`}
        <span className="text-gray-400"> · </span>{listing.type}
        {listing.place && <><span className="text-gray-400"> · </span>{listing.place}</>}
      </div>
      <div className="text-xs text-gray-500 truncate">
        {listing.integration ? <>via {listing.integration.name || listing.integration.type}{listing.integration.file && <span className="font-mono"> ({listing.integration.file})</span>}</> : 'added by hand'}
        {listing.sourceUrl && <> · <a href={listing.sourceUrl} target="_blank" rel="noreferrer" className="underline">source</a></>}
      </div>
    </div>
  );
}

function FlagRow({ flag, api, toast, onChange }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);

  const act = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      if (message) toast(message);
      await onChange();
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = (status) => act(() => api(`/admin/listing-flags/${flag.id}`, { method: 'PATCH', body: { status } }));
  const remove = async () => {
    if (!await confirm({ title: 'Delete this flag?', message: 'The listing itself is not changed.', confirmLabel: 'Delete flag', danger: true })) return;
    act(() => api(`/admin/listing-flags/${flag.id}`, { method: 'DELETE' }), 'Flag deleted.');
  };

  return (
    <li className="py-3 flex flex-col md:flex-row md:items-start gap-3">
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge color={STATUS_COLORS[flag.status]}>{flag.status}</Badge>
          {flag.reasons.map((reason) => <Badge key={reason} color="orange">{reasonLabel(reason)}</Badge>)}
          <span className="text-xs text-gray-500">{timeAgo(flag.createdAt)}{flag.flaggedBy && ` by @${flag.flaggedBy}`}</span>
        </div>
        <ListingSummary listing={flag.listing} />
        {flag.duplicateOfUuid && <div className="pl-3 border-l-2 border-gray-200"><ListingSummary listing={flag.duplicateOf} label="duplicate of" /></div>}
        {flag.note && <p className="text-sm bg-amber-50 border border-amber-200 rounded px-2 py-1">{flag.note}</p>}
      </div>
      <div className="flex flex-wrap gap-2 md:justify-end shrink-0">
        {flag.listing && <Link to={`/listing/${flag.listing.uuid}/edit`} className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50">Edit listing</Link>}
        {flag.status === 'open' ? (
          <>
            <Button variant="success" busy={busy} onClick={() => setStatus('fixed')}>Fixed</Button>
            <Button busy={busy} onClick={() => setStatus('dismissed')}>Dismiss</Button>
          </>
        ) : (
          <Button busy={busy} onClick={() => setStatus('open')}>Reopen</Button>
        )}
        <Button variant="danger" busy={busy} onClick={remove}>Delete</Button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Auto-detected suspects
// ---------------------------------------------------------------------------
function Suspects({ api, toast, onFlagged }) {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [done, setDone] = useState(() => new Set()); // suspects handled this session

  const scan = async () => {
    setScanning(true);
    try {
      setData(await api('/admin/listing-flags/suspects'));
      setDone(new Set());
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setScanning(false);
    }
  };

  const mark = async (key, body, message) => {
    try {
      await api('/admin/listing-flags', { method: 'POST', body });
      setDone((current) => new Set(current).add(key));
      toast(message);
      onFlagged();
    } catch (error) {
      toast(error.message, 'error');
    }
  };

  const allDay = data ? data.allDay.filter((listing) => !done.has(`day:${listing.uuid}`)) : [];
  const duplicates = data ? data.duplicates.filter((pair) => !done.has(`dup:${pair.listing.uuid}:${pair.duplicateOf.uuid}`)) : [];

  return (
    <Card
      title="Suspects"
      actions={<Button busy={scanning} onClick={scan}>{data ? 'Rescan' : 'Scan upcoming listings'}</Button>}
    >
      {!data && (
        <p className="text-sm text-gray-500">
          Looks through upcoming listings for single-day “all day” times (12:00 AM → 11:59 PM, usually a scraper that missed the time)
          and likely duplicates (same day, same place or source URL, similar names).
        </p>
      )}
      {data && (
        <div className="space-y-6">
          <p className="text-xs text-gray-500">Scanned {data.scanned} listings in the next {data.windowDays} days.</p>

          <section>
            <h4 className="font-semibold mb-2">
              Likely duplicates <span className="text-gray-500 font-normal">({duplicates.length}{data.duplicatesTotal > data.duplicates.length ? ` of ${data.duplicatesTotal}` : ''})</span>
            </h4>
            {duplicates.length === 0 && <p className="text-sm text-gray-500">None found.</p>}
            <ul className="divide-y divide-gray-100">
              {duplicates.map((pair) => {
                const key = `dup:${pair.listing.uuid}:${pair.duplicateOf.uuid}`;
                const body = { listing: pair.listing.uuid, duplicateOf: pair.duplicateOf.uuid, reasons: ['duplicate'], note: `Auto-detected: ${pair.signals.join(', ')}` };
                return (
                  <li key={key} className="py-3 space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {pair.signals.map((signal) => <Badge key={signal} color="blue">{signal}</Badge>)}
                      {pair.alreadyFlagged && <Badge color="amber">one is already flagged</Badge>}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <ListingSummary listing={pair.listing} label="newer" />
                      <ListingSummary listing={pair.duplicateOf} label="older" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="danger" onClick={() => mark(key, body, 'Flagged as a duplicate.')}>Flag newer as duplicate</Button>
                      <Button onClick={() => mark(key, { ...body, status: 'dismissed', note: 'Checked: not a duplicate' }, 'Saved as not a duplicate.')}>Not a duplicate</Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">
              “All day” times <span className="text-gray-500 font-normal">({allDay.length}{data.allDayTotal > data.allDay.length ? ` of ${data.allDayTotal}` : ''})</span>
            </h4>
            {allDay.length === 0 && <p className="text-sm text-gray-500">None found.</p>}
            <AllDayList listings={allDay} mark={mark} />
          </section>
        </div>
      )}
    </Card>
  );
}

// Grouped by integration, since one broken scraper usually produces a run of these
function AllDayList({ listings, mark }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const listing of listings) {
      const name = listing.integration ? (listing.integration.name || listing.integration.type) : 'Added by hand';
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(listing);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [listings]);

  return (
    <div className="space-y-4">
      {groups.map(([name, items]) => (
        <div key={name}>
          <div className="text-sm font-medium text-gray-700 mb-1">{name} <span className="text-gray-500 font-normal">({items.length})</span></div>
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded">
            {items.map((listing) => {
              const key = `day:${listing.uuid}`;
              return (
                <li key={key} className="p-2 flex flex-col md:flex-row md:items-center gap-2">
                  <div className="flex-1 min-w-0"><ListingSummary listing={listing} /></div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="danger" onClick={() => mark(key, { listing: listing.uuid, reasons: ['wrong_time'], note: 'Listed as all day; check the source for the real time' }, 'Flagged wrong time.')}>Wrong time</Button>
                    <Button onClick={() => mark(key, { listing: listing.uuid, reasons: ['wrong_time'], status: 'dismissed', note: 'Checked: really all day' }, 'Marked as fine.')}>Really all day</Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default AdminFlags;
